import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { Trash2, UserPlus, Zap, Link2 } from "lucide-react";

export default function Team() {
    const { t } = useLang();
    const { user } = useAuth();
    const [team, setTeam] = useState(null);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [inviteLink, setInviteLink] = useState("");

    const fetchTeam = () => api.get("/team").then((r) => setTeam(r.data)).catch(() => {});

    useEffect(() => { fetchTeam(); }, []);

    useEffect(() => {
        if (team?.is_owner) {
            api.get("/team/invite-link").then((r) => setInviteLink(r.data.url)).catch(() => {});
        }
    }, [team?.is_owner]);

    const copyInviteLink = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            toast.success(t("team.invite_link_copied"));
        } catch (e) {
            toast.error(t("common.error"));
        }
    };

    const invite = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/team/invite", { email });
            toast.success("OK");
            setEmail("");
            fetchTeam();
        } catch (err) {
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally { setLoading(false); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/team/member/${id}`);
            fetchTeam();
        } catch (e) { toast.error(t("common.error")); }
    };

    if (!team) return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <p className="p-10 text-slate-500">{t("common.loading")}</p>
        </div>
    );

    if (team.plan !== "studio") return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <div className="max-w-2xl mx-auto px-6 py-20">
                <h1 className="font-display text-4xl mb-4">{t("team.studio_only")}</h1>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
                <div className="mono-label mb-3">/ TEAM</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-12" data-testid="team-title">
                    {t("team.title")}
                </h1>

                <div className="grid lg:grid-cols-3 gap-px bg-slate-100 border border-slate-200 mb-12">
                    <div className="bg-ink p-8 lg:col-span-2 border-l-2 border-brand">
                        <div className="mono-label mb-4">/ {t("team.owner")}</div>
                        <div className="font-display text-2xl">{team.owner.name}</div>
                        <div className="text-slate-500 text-sm">{team.owner.email}</div>
                    </div>
                    <div className="bg-ink p-8">
                        <div className="mono-label mb-4 flex items-center gap-2"><Zap size={12} className="text-brand" /> {t("team.pool")}</div>
                        <div className="font-display text-5xl text-brand" data-testid="team-pool-tokens">{team.tokens_pool}</div>
                    </div>
                </div>

                {team.is_owner && (
                    <form onSubmit={invite} className="flex gap-3 mb-10" data-testid="invite-form">
                        <input
                            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("team.invite_placeholder")}
                            className="flex-1 bg-transparent border border-slate-300 px-4 py-3 rounded-sm focus:border-brand outline-none transition-colors"
                            data-testid="invite-email-input"
                        />
                        <button type="submit" disabled={loading} className="btn-primary" data-testid="invite-submit-btn">
                            <UserPlus size={16} /> {t("team.invite")}
                        </button>
                    </form>
                )}

                {team.is_owner && inviteLink && (
                    <div className="card-surface p-5 mb-10 flex items-center justify-between gap-4 flex-wrap" data-testid="invite-link-card">
                        <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
                            <Link2 size={14} className="text-brand flex-shrink-0" />
                            <span className="mono-label text-slate-400 flex-shrink-0">{t("team.invite_link_title")}</span>
                            <span className="truncate font-mono text-xs text-slate-500" data-testid="invite-link-text">{inviteLink}</span>
                        </div>
                        <button onClick={copyInviteLink} className="btn-ghost text-sm flex-shrink-0" data-testid="invite-link-copy-btn">
                            {t("team.invite_link_copy")}
                        </button>
                    </div>
                )}

                <div>
                    <div className="mono-label mb-4">/ {t("team.members")} ({team.members.length}/{team.max_members})</div>
                    {team.members.length === 0 && (
                        <p className="text-slate-500">{t("team.max_5")}</p>
                    )}
                    <div className="border border-slate-200 divide-y divide-slate-200" data-testid="team-members-list">
                        {team.members.map((m) => (
                            <div key={m.id} className="px-6 py-4 flex items-center justify-between">
                                <div>
                                    <div className="font-display text-lg">{m.name}</div>
                                    <div className="text-slate-500 text-sm">{m.email}</div>
                                </div>
                                {team.is_owner && (
                                    <button onClick={() => remove(m.id)} className="text-slate-500 hover:text-brand transition-colors" data-testid={`remove-member-${m.id}`}>
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
